import { inject, Injectable } from '@angular/core';
import each from 'lodash-es/each';
import { RequestService } from '@df/api/request/request.service';
import { ConfigResource, IConfigModel } from '@df/catalog/config/config.resource';
import { App } from '@df/core/app';
import { Client } from '@df/core/service/client';
import { CookiesService } from '@df/core/service/cookies';
import { Logger } from '@df/core/service/logger';
import { Ab } from '@df/module-ab/ab.namespace';
import { AB_OPTIONS } from '@df/module-ab/ab.tokens';
import { TimeoutService } from '@df/ng/timeout.service';
import { CustomerModel } from '@df/session/api/customer.model';
import difference from 'lodash-es/difference';
import find from 'lodash-es/find';
import findIndex from 'lodash-es/findIndex';
import findKey from 'lodash-es/findKey';
import groupBy from 'lodash-es/groupBy';
import keyBy from 'lodash-es/keyBy';
import map from 'lodash-es/map';
import sortBy from 'lodash-es/sortBy';
import { BehaviorSubject, Subject } from 'rxjs';
import { Acl } from '@df/core/service/acl';
import { DfEventService } from '@df/core/service/df-event';

declare module '@df/core/app' {
    interface App {
        ab: AbService;
    }
}

declare module '@df/session/customer/customer.interfaces' {
    namespace Customer {
        interface ICustomerData {
            exData?: { [expId: string]: string };
        }
    }
}

export interface ConvertEvent {
    data: {
        experience_id?: string;
        variation_id?: string;
        experience_name?: string;
        variation_name?: string;
        activated_first_time?: boolean; // with `experience.activated`
        goal_id?: string; // with `goal.triggered`
        location_id?: string; // with `location.triggered`
        location_name?: string; // with `location.triggered`
    };
    event:
        | 'experience.variation_decided'
        | 'experience.activated'
        | 'goal.triggered'
        | 'snippet.experiences_evaluated'
        | 'snippet.goals_evaluated'
        | 'cookies.saved'
        | 'snippet.goals_evaluated'
        | 'location.triggered';
}

@Injectable({
    providedIn: 'root'
})
export class AbService {
    readonly change$ = new Subject<{ expId: string; variant: Ab.TVariant }>();

    protected readonly subjectsMap = new Map<string, BehaviorSubject<Ab.IExperimentState>>();

    protected runningExperiments: { [experimentId: string]: boolean } = {};

    protected experimentsConfig?: { [experimentName: string]: Ab.IExperimentDef };
    private enforcedVariants = new Map<string, Ab.TVariant>();

    protected initiated = false; // true if init done and sucessful
    protected ready = false; // true if init finished

    protected pendingVariants: { experimentKey: string; variant: number }[] = [];
    private onReadyCallbacks: (() => void)[] = [];

    private _adobeTargetExperiments?: Ab.IExperimentDef[];

    private loadAdobeTargetOffersDebounced?: () => void;

    private loadedAdobeTargetActivityIds: string[] = [];

    private initPromise?: Promise<void>;

    protected app = inject(App);
    readonly abConfig = inject(AB_OPTIONS);
    protected logger = inject(Logger);
    protected request = inject(RequestService);
    protected client = inject(Client);
    protected customerModel = inject(CustomerModel);
    protected configResource = inject(ConfigResource);
    protected timeoutService = inject(TimeoutService);
    private cookieService = inject(CookiesService);
    private aclService = inject(Acl);
    private dfEventService = inject(DfEventService);

    /**
     * Initializes the service on `APP_INITIALIZER`
     */
    appInitialize() {
        this.app.ab = this;

        if (this.app.isPlatformBrowser && !this.client.isPrerender) {
            this.load();
        } else {
            this.markAsReady();
        }

        if (this.abConfig.convertcom?.trackRevenue) {
            this.dfEventService.success(event => {
                if (event.id === 'nexusCheckoutSuccess') {
                    const successData = event.result?.success;
                    if (successData?.tracking) {
                        this.onReady(() => {
                            this.trackRevenue(successData.totals.grand_total, successData.qty, 'revenue');
                        });
                    }
                }
            });
        }
    }

    pushToConvertQueue(what: string[] | { what: string; params: any }): void {
        window['_conv_q'] = window['_conv_q'] || [];
        window['_conv_q'].push(what);
    }

    private async load() {
        if (this.ready) {
            return;
        }
        if (!this.initPromise) {
            this.initPromise = this.init();
        }
        return this.initPromise;
    }

    protected async init() {
        try {
            const config = (await this.configResource.loadModelByUrl(Ab.configId, { safe: true })) as IConfigModel<Ab.IConfigContent>;
            if (!config) {
                this.markAsReady();
                return;
            }
            const experiments = config.data?.content.experiments || [];
            experiments.forEach(experiment => {
                if (typeof experiment.key === 'string') {
                    experiment.key = experiment.key.replace(/[^a-zA-Z0-9]/g, '');
                }
                if (typeof experiment.name === 'string') {
                    experiment.name = experiment.name.trim();
                }
            });

            this.experimentsConfig = keyBy(experiments, 'name');

            if (find(this.experimentsConfig, i => typeof i !== 'string' && i.engine === 'convertcom')) {
                this.setupConvertCallback();
                await this.loadConvert();
            }

            if (this.abConfig.adobeTarget?.enabled) {
                this.loadAdobeTargetOffersDebounced = this.timeoutService.debounce(() => this.loadAdobeTargetOffers(), 100);
                this.setupAdobeTarget();
            }
            // forced variants from cookies
            if (this.aclService.isDeveloper) {
                experiments?.forEach(experiment => {
                    const cookieVariant = this.cookieService.get(`ab-${experiment.name}`);
                    if (['A', 'B', 'C', 'D', 'E', 'F', 'G'].includes(cookieVariant)) {
                        console.log('[info] forced variant from cookie: ' + experiment.name + ' - ' + cookieVariant);
                        this.setVariant(experiment.name, cookieVariant as Ab.TVariant);
                    }
                });
            }

            this.initiated = true;
            this.markAsReady();
            this.pendingVariants.forEach(({ experimentKey, variant }) => this.onVariant(experimentKey, variant));
            this.customerModel.obs.data.subscribe(() => this.applyBackendSession(true));
        } catch (err) {
            this.logger.error(err);
            this.markAsReady();
        }
    }

    private markAsReady() {
        if (!this.ready) {
            this.ready = true;
            this.onReadyCallbacks.forEach(cb => cb());
        }
    }

    getSubject(experimentId: string): BehaviorSubject<Ab.IExperimentState> {
        if (!this.subjectsMap.has(experimentId)) {
            this.subjectsMap.set(experimentId, new BehaviorSubject<Ab.IExperimentState>({ variant: 'A', isInitial: true }));
        }

        return this.subjectsMap.get(experimentId)!;
    }

    isRunning(experimentName: string) {
        return !!this.runningExperiments[experimentName];
    }

    async runExperiment(name: string, promiseTimeout = 2000): Promise<Ab.TVariant> {
        await this.load();
        // if experiment is not found, return A variant
        if (!this.getExpById(name)) {
            return 'A';
        }
        this.assureExperimentIsRunning(name);
        const currentVariant = this.getVariant(name);
        if (!this.initiated || this.isRunning(name) || currentVariant !== 'A') {
            return currentVariant;
        }
        let finished = false;
        return new Promise((resolve, reject) => {
            const subscription = this.change$.subscribe(({ expId, variant }) => {
                if (expId === name && !finished) {
                    resolve(variant);
                    finished = true;
                    subscription.unsubscribe();
                }
            });
            this.timeoutService.setTimeout(() => {
                if (!finished) {
                    reject(new Error('Experiment timeout'));
                    finished = true;
                    subscription.unsubscribe();
                }
            }, promiseTimeout);
        });
    }

    private assureExperimentIsRunning(name: string): void {
        if (this.app.isPlatformBrowser) {
            this.onReady(() => {
                if (this.initiated) {
                    const experiment = this.getExpById(name);
                    const key = experiment?.key;

                    if (key && !window[`runExperiment${key}`]) {
                        window[`runExperiment${key}`] = 1;

                        this.pushToConvertQueue(['executeExperiment', key]);

                        this.updateConvertExperiments();
                    }
                }
            });
        }
    }

    /**
     * Sets up convert.com callback on code run
     */
    protected setupConvertCallback(): void {
        this.pushToConvertQueue({
            what: 'addListener',
            params: {
                event: 'experience.activated',
                handler: (event: ConvertEvent) => {
                    this.updateConvertExperiments();
                }
            }
        });
    }

    protected onVariant(experimentKey: string, variant: number, missing?: boolean): void {
        if (!this.initiated) {
            this.pendingVariants.push({ experimentKey, variant });
        } else {
            const id = this.getExpIdByKey(experimentKey);

            if (id) {
                this.runningExperiments[id] = true;
                this.setExperimentVariant(id, variant, missing);
            } else {
                this.logger.warn('AB exp called but not set: ' + experimentKey);
            }
        }
    }

    onReady(cb: () => void) {
        if (this.ready) {
            cb();
        } else {
            this.onReadyCallbacks.push(cb);
        }
    }

    /**
     * @deprecated This method is deprecated. Use setExperimentVariant instead. This method is for developers only
     */
    setVariant(expId: string, variantId: Ab.TVariant) {
        this.enforcedVariants.set(expId, variantId);
        this.setExperimentVariant(expId, variantId);
    }
    /**
     * Sets variant version
     */
    setExperimentVariant(expId: string, variantId: Ab.TVariant | number, missing?: boolean) {
        let variant = typeof variantId === 'number' ? this.variantToStr(variantId) : variantId;
        //    use forced if set
        if (this.enforcedVariants.has(expId)) {
            variant = this.enforcedVariants.get(expId)!;
        }
        const state: Ab.IExperimentState = {
            variant,
            isInitial: false
        };

        if (typeof missing === 'boolean') {
            state.missing = missing;
        }
        const subject = this.getSubject(expId);
        if (subject.value !== state) {
            subject.next(state);
            this.change$.next({ expId, variant });
        }

        if (this.getExpById(expId)?.backend) {
            this.applyBackendSession();
        }

        this.logger.log('AB:' + variant + ' ' + expId, false, true);
    }

    /**
     * Takes letter style variant by human readable exp id
     */
    getVariant(id: string) {
        return this.getSubject(id).value.variant;
    }

    /**
     * Takes human readable experiment id (see config file) by experiment code
     */
    protected getExpIdByKey(experimentKey: string): string | undefined {
        return findKey(this.experimentsConfig, entry => this.entryToExpId(entry) == experimentKey);
    }

    /**
     * Converts entry to experiment code
     * @param entry Experiment config entry
     * @returns
     */
    protected entryToExpId(entry: Ab.IExperimentDef | string): string {
        return typeof entry === 'string' ? entry : entry.key;
    }

    /**
     * Takes experiment code by human readable experiment id
     */
    getExpById(id: string): Ab.IExperimentDef | undefined {
        const entry = this.experimentsConfig?.[id];

        return typeof entry === 'string' ? { key: entry, name: entry } : entry;
    }

    /**
     * Converts variant id (like 1, 2, 3) to string version like A, B ,C
     */
    protected variantToStr(variant: number): Ab.TVariant {
        return 'ABCDEFGHIJ'[variant] as Ab.TVariant;
    }

    /**
     * Sync backend session with FE variants
     * @param soft If true, will not send data if it's already set
     */
    // eslint-disable-next-line @typescript-eslint/member-ordering
    protected applyBackendSession = this.timeoutService.debounce((soft = false) => {
        let items = map(this.experimentsConfig, (entry, id) => {
            if (typeof entry === 'object' && entry.backend) {
                return { id, opt: this.getVariant(id) };
            }
            return false;
        }).filter(i => !!i) as {
            id: string;
            opt: Ab.TVariant;
        }[];

        if (soft) {
            const existingData = this.customerModel.data.exData || {};
            items = items.filter(i => existingData[i.id] === i.opt);
        }
        if (items.length) {
            this.request
                .send(undefined, {
                    path: 'ex/apply',
                    data: { items }
                })
                .catch(err => {
                    this.logger.error(err);
                });
        }
    }, 100);

    status(): Ab.IExperimentStatusInfo[] {
        const convertExperiments = this.retrieveConvertExperiments();
        return map(this.experimentsConfig, (exp, expId) => {
            if (!expId) {
                return;
            }
            const convertExperiment = exp.engine === 'convertcom' ? convertExperiments?.find(i => (i.experimentId = expId)) : undefined;
            const convertVariants = convertExperiment ? Object.values(convertExperiment?.variants || {}).map(i => i.variant) : undefined;

            return {
                name: expId,
                key: exp.key,
                variant: this.getVariant(expId),
                active: this.isRunning(expId),
                variants: convertVariants || Array.from({ length: 3 }, (_, i) => this.variantToStr(i))
            };
        }).filter(i => !!i);
    }

    help() {
        const status = this.status()
            .map(info => {
                return `${info.name} - ${info.variant ? 'ON, variant: (' + info.variant + ')' : 'OFF'}`;
            })
            .join('\n');
        console.log(
            `AB service
        id - is human readable experiment identifier set by developer
        key - is opptimize experiment key
        Helpful commands:
        1. isRunning - checks if experiment identified by 'experimentName' is running
        this.app.ab.isRunning('experimentName');
        2. Retrieve experiment key by its id
        this.app.ab.getExpKeyByid('experimentName');
        3. Retrieve experiment id by its key
        this.app.ab.getExpIdByKey('HQlMp46JQ0il9zcXwghduw');
        4. Get current experiment variant
        this.app.ab.getVariant('experimentName');
        5. Set current experiment variant
        this.app.ab.setVariant('experimentName','B');
        - it will swtich experiment on if wasn't running before
        - it will only persist till refreshing the browser tab

        Magic cookies:
        - ab-<experimentName> - forces variant set in cookie for experiment (must be in developer mode)

        Current experiments list:
        ${status || 'No experiments set'}
        `
        );
    }

    /**
     * Loads convert.com script
     */
    protected async loadConvert() {
        const gen = this.abConfig.convertcom?.script;
        const script = gen && gen(this.app);

        if (script) {
            await this.request.loadScriptAsync(script);
            this.updateConvertExperiments();
            // Needed to get convert script know that url changed so it can activate experiments enabled on particular url.
            this.app.meta.subscribe(() => {
                this.pushToConvertQueue(['run', 'true']);
                this.updateConvertExperiments();
            });
        }
    }

    protected updateConvertExperiments(): void {
        const convertData = this.retrieveConvertExperiments();

        if (convertData) {
            for (const entry of convertData) {
                const variant = entry.variantId ? entry.variants[entry.variantId] : undefined;
                if (variant) {
                    this.onVariant(entry.experimentId, variant.index);
                } else {
                    this.runningExperiments[entry.experimentId] = true; // no variant found, but the experiment is set to run (probably not in audience)
                }
            }
        }
    }

    triggerGoal(name: string) {
        const goalId = this.getGoalId(name);
        if (goalId) {
            this.pushToConvertQueue(['triggerConversion', goalId]);
        } else {
            console.warn('Goal not found: ' + name);
        }
    }

    trackRevenue(amount: number, qty: number, goalName: string, forceMultiple?: boolean) {
        const goalId = this.getGoalId(goalName);
        if (goalId) {
            const data = ['pushRevenue', amount.toFixed(2), qty.toString(), goalId];
            if (forceMultiple) {
                data.push('force_multiple');
            }
            this.pushToConvertQueue(data);
        } else {
            console.warn('Goal not found: ' + name);
        }
    }

    private getGoalId(key: string) {
        const lowerKey = key.toLowerCase();
        return window.convert?.data.goals?.find(g => g.key?.toLowerCase() === lowerKey || g.name?.toLowerCase() === lowerKey)?.id;
    }

    retrieveConvertExperiments(): Ab.IExperimentStatus[] | undefined {
        const convert = window.convert;
        const data = convert?.data;
        if (!data) {
            return undefined;
        }
        const experiences = data.experiences;
        if (experiences) {
            return experiences.map(experience => {
                const variation = convert?.currentData?.experiences?.[experience.id]?.variation;
                return {
                    experimentId: experience.id,
                    experimentName: experience.name,
                    variantId: variation?.id,
                    variants:
                        keyBy(
                            experience?.variations?.map((v, index) => ({
                                id: v.id,
                                variant: this.variantToStr(index),
                                name: v.name,
                                index
                            })),
                            'id'
                        ) || {}
                };
            });
        }
        const experiments = data.experiments;
        if (experiments) {
            return Object.values(experiments).map(experiment => {
                const experimentId = experiment.id;
                const current = convert?.currentData?.experiments?.[experimentId];
                const variantId = current?.variation_id;
                let index = 0;
                return {
                    experimentId,
                    variantId,
                    variants:
                        keyBy(
                            map(experiment.vars, (v, id) => ({
                                id,
                                index,
                                variant: this.variantToStr(index++),
                                name: v.name
                            })),
                            'id'
                        ) || {}
                };
            });
        }
    }

    private setupAdobeTarget(): void {
        if (this.validAdobeTargetExperiments?.length && this.loadAdobeTargetOffersDebounced) {
            this.loadAdobeTargetOffersDebounced();
            this.app.meta.subscribe(() => this.loadAdobeTargetOffersDebounced?.());
        }
    }

    private loadAdobeTargetOffers(): void {
        const experiments = this.validAdobeTargetExperiments.filter(i => this.loadedAdobeTargetActivityIds.indexOf(i.key) === -1);

        if (experiments?.length) {
            const withParameters = experiments.filter(i => !!this.getAdobeParametersFromExperimentDef(i));
            const withoutParameters = difference(experiments, withParameters);
            if (withoutParameters?.length) {
                this.getAdobeTargetExperiments().then(response => this.onAdobeTargetOffersLoad(response, withoutParameters));
            }
            if (withParameters?.length) {
                const grouped = groupBy(withParameters, exp => {
                    const parameters = this.getAdobeParametersFromExperimentDef(exp);
                    return parameters ? JSON.stringify(parameters) : undefined;
                });

                const groupes = Object.values(grouped);
                groupes.forEach(group => {
                    const exp = group[0];
                    if (exp) {
                        const parameters = this.getAdobeParametersFromExperimentDef(exp);
                        this.getAdobeTargetExperiments(parameters).then(response => this.onAdobeTargetOffersLoad(response, group));
                    }
                });
            }
        }
    }

    private getAdobeParametersFromExperimentDef(experiment: Ab.IExperimentDef): { [key: string]: string } | undefined {
        const parameters = sortBy(
            experiment.adobetarget_parameters?.filter(i => !!i.parameter_name && !!i.parameter_value) || [],
            i => i.parameter_name
        );
        if (parameters?.length) {
            const parametersObj: { [key: string]: string } = {};
            parameters.forEach(i => {
                parametersObj[i.parameter_name] = i.parameter_value;
            });
            return parametersObj;
        }
        return undefined;
    }

    private onAdobeTargetOffersLoad(
        offersTokens: Ab.IAdobeTargetGetOffersResponseDetailsResponseTokens[],
        experiments: Ab.IExperimentDef[]
    ): void {
        experiments.forEach(experiment => {
            const activityId = experiment.key;
            if (activityId && this.loadedAdobeTargetActivityIds.indexOf(activityId) === -1) {
                const offer = offersTokens.find(i => i['activity.id'] === activityId);
                if (offer) {
                    this.loadedAdobeTargetActivityIds.push(activityId);
                    const variant = this.getVariantFromAdobeTargetOffer(experiment, offer);
                    this.onVariant(activityId, variant || 0);
                } else {
                    this.onVariant(activityId, 0, true);
                }
            }
        });
    }

    private getVariantFromAdobeTargetOffer(
        experiment: Ab.IExperimentDef,
        offer: Ab.IAdobeTargetGetOffersResponseDetailsResponseTokens
    ): number | undefined {
        const activityId = offer['activity.id'];
        const variantId = offer['experience.name'];
        if (activityId && variantId) {
            const variants = experiment.adobetarget_variants;
            if (variants && variants.length > 1) {
                const variantIndex = findIndex(
                    variants,
                    variant => variant.variant_name.toLowerCase().trim() === variantId.toLowerCase().trim()
                );
                if (variantIndex > -1) {
                    return variantIndex;
                }
            }
        }
        return undefined;
    }

    private get validAdobeTargetExperiments(): Ab.IExperimentDef[] {
        if (!this._adobeTargetExperiments) {
            const experiments = Object.values(this.experimentsConfig || {});
            this._adobeTargetExperiments = experiments.filter(
                i =>
                    typeof i !== 'string' &&
                    i.engine === 'adobetarget' &&
                    (i.adobetarget_variants?.filter(j => j.variant_name)?.length || 0) > 1
            );
        }
        return this._adobeTargetExperiments;
    }

    private async getAdobeTargetExperiments(parameters?: {
        [key: string]: string;
    }): Promise<Ab.IAdobeTargetGetOffersResponseDetailsResponseTokens[]> {
        if (typeof window !== 'undefined' && window.adobe?.target) {
            try {
                const data: Ab.IAdobeTargetGetOffersData = {
                    request: {
                        execute: {
                            pageLoad: {}
                        }
                    }
                };
                if (parameters && data.request.execute?.pageLoad) {
                    data.request.execute.pageLoad.parameters = { ...parameters };
                }
                const response = await window.adobe?.target?.getOffers(data);
                const responseTokens = response?.execute?.pageLoad?.options?.map(i => i.responseTokens).filter(i => !!i);
                return responseTokens || [];
            } catch (e) {
                return [];
            }
        }
        return [];
    }
}
